import { validatedDisplayView } from "../i18n/display-view.js";
import { portalText } from "../i18n/portal.js";
/**
 * What Compliance and Audit log render (P14-05, P14-06).
 *
 * Three things these types are careful about:
 *
 * **A retention select is a list of offers plus the one chosen, not a number.** The four
 * classes have different option sets — recordings offer 7/30/90 days and 12 months, orders
 * and survey answers offer "keep until I delete them" — and a screen that rendered a free
 * number field would let somebody type a value the sweep has no policy for.
 * {@link RETAIN_FOREVER_DAY} is how "keep until I delete them" is carried, because a null
 * would read as "unset" and the first `?? 30` would delete a year of orders.
 *
 * **Operator access is a switch with an audit promise attached.** The copy beside it is a
 * commitment — every play is written to the customer's own audit log with the operator's name
 * — so the view carries `lastPlayedByOperatorText`, which is how the customer can see that
 * the promise is being kept rather than being told that it is.
 *
 * **A person-data request has a deadline, and the view carries where it stands.** AC-34 is 24
 * hours; `progress` distinguishes "still inside the window" from "late", so the screen does
 * not alert on every request the moment it is made.
 */
import type { Money } from "@wetalk/domain";

// ---------------------------------------------------------------------------------------
// Compliance (P14-05)
// ---------------------------------------------------------------------------------------

export type MediumView = "voice" | "web_chat" | "whatsapp" | "viber";

/** One consent line, per surface. The design shows three surfaces. */
export interface ConsentLineRowView {
  readonly consentLineId: string;
  readonly medium: MediumView;
  /** "Inbound calls", "Outbound campaigns", "Web chat and messaging". */
  readonly surfaceLabel: string;
  readonly text: string;
  readonly enabled: boolean;
  /** "On", or "Shown in the widget" for a medium that displays rather than speaks it. */
  readonly stateLabel: string;
  /** How many of the agent's languages have this line translated. */
  readonly languageCount: number;
}

export type RetentionClassView =
  | "recording"
  | "transcript"
  | "order"
  | "survey_result"
  | "caller_detail"
  | "audit";

/** The sentinel for "keep until I delete them". Never `null`, for the reason in the header. */
export const RETAIN_FOREVER_DAY = -1;

export interface RetentionOptionView {
  readonly retainDay: number;
  /** "30 days", "12 months", "Keep until I delete them". */
  readonly label: string;
}

export interface RetentionSelectView {
  readonly class: RetentionClassView;
  readonly label: string;
  readonly note: string;
  readonly option: readonly RetentionOptionView[];
  readonly chosenRetainDay: number;
  /** The product policy's period for the class — what it is kept for when nobody has chosen. */
  readonly defaultRetainDay: number;
}

/** What the next retention sweep would remove if a class were kept for `retainDay` days. */
export interface RetentionPreviewView {
  readonly retentionClass: RetentionClassView;
  readonly retainDay: number;
  /** False for a class the sweep keeps (orders, survey answers, the audit log): nothing goes. */
  readonly swept: boolean;
  /** Recordings, transcripts (per conversation) or caller numbers. */
  readonly affectedCount: number;
}

export interface OperatorAccessView {
  readonly enabled: boolean;
  readonly changedText: string;
  readonly changedByName: string | null;
  /** `null` when no operator has ever opened a recording in this workspace. */
  readonly lastPlayedByOperatorText: string | null;
}

/** The outbound caller-ID card. Nothing here is guessed from the number. */
export interface OutboundCallerIdView {
  readonly e164: string;
  readonly brandedName: string;
  /** Whether the carrier has confirmed the branding, which is not the same as registered. */
  readonly brandingVerified: boolean;
  readonly note: string;
}

export type PersonDataRequestKindView = "export" | "delete";

export type PersonDataRequestProgressView =
  | { readonly state: "pending"; readonly remainingHour: number }
  | { readonly state: "overdue"; readonly overdueHour: number }
  | { readonly state: "completed"; readonly withinDeadline: boolean; readonly elapsedHour: number };

export interface PersonDataRequestRowView {
  readonly personDataRequestId: string;
  readonly kind: PersonDataRequestKindView;
  /** What was typed, masked the way the row is: never the full number for a Viewer. */
  readonly searchTermText: string;
  readonly requestedByName: string;
  readonly requestedText: string;
  readonly progress: PersonDataRequestProgressView;
  /** Per store, because they are deleted independently and fail independently. */
  readonly affectedCount: Readonly<Record<string, number>> | null;
  /**
   * A completed export's file (customer-portal review item 197): `kept` until the export
   * container's seven-day rule removes it, then `removed`; `null` for a deletion or a request
   * still open. The link itself is asked for when the button is pressed — reading the screen
   * never mints one, and each download is audited.
   */
  readonly exportFile:
    | { readonly state: "kept"; readonly untilText: string }
    | { readonly state: "removed" }
    | null;
}

/** A short-lived link to one completed export's zip (review item 197). */
export interface PersonDataExportLinkView {
  readonly downloadUrl: string;
  /** "in 15 minutes": when the link, not the file, stops working. */
  readonly expiresAtText: string;
}

/**
 * What happens on a call today (customer-portal review #66): which live agents record, which do
 * not, and the notice the runtime speaks before recording, per language.
 * `null` from an API that predates it.
 *
 * AMENDED 2026-09-26 (review #204): the notice is the workspace's own wording when it has saved one,
 * else the language pack's standard line, and it can be changed from the screen.
 */
export interface RecordingInForceView {
  readonly recordingVoiceAgent: readonly string[];
  readonly notRecordingVoiceAgent: readonly string[];
  readonly spokenNotice: readonly SpokenNoticeView[];
}

/** One language's notice (review #204). */
export interface SpokenNoticeView {
  readonly languageCode: string;
  readonly languageName: string;
  /** The line in force. */
  readonly text: string;
  /** Whose wording `text` is: the workspace's own, or WeTalk's standard line. */
  readonly source: "workspace" | "standard";
  /** The language pack's standard line; `null` when the pack has none to offer. */
  readonly standardText: string | null;
  /** Recording agents whose live version still says something other than `text`. */
  readonly outOfDateVoiceAgent: readonly string[];
}

/** A consent-line save (review #204): the screen after it, and who now says it. */
export interface ConsentLineSavedView {
  readonly compliance: ComplianceView;
  readonly republishedVoiceAgent: readonly string[];
}

/** The longest line the API accepts, in characters. Stated by the API's spec; the field counts. */
export const CONSENT_LINE_MAX_LENGTH = 300;

export interface ComplianceView {
  readonly workspaceName: string;
  readonly recording: RecordingInForceView | null;
  readonly consentLine: readonly ConsentLineRowView[];
  readonly retention: readonly RetentionSelectView[];
  readonly operatorAccess: OperatorAccessView;
  readonly outboundCallerId: OutboundCallerIdView | null;
  readonly suppressedCount: number;
  readonly personDataRequest: readonly PersonDataRequestRowView[];
  /** AC-34's promise, in the unit the copy states it in. */
  readonly personDataDeadlineHour: number;
}

export interface PersonDataRequestDraftView {
  readonly kind: PersonDataRequestKindView;
  /** A phone number or an email address, as typed. Hashed before it is stored. */
  readonly searchTerm: string;
}

// ---------------------------------------------------------------------------------------
// Audit log (P14-06)
// ---------------------------------------------------------------------------------------

/** The five headings the filter offers beside "Everything" (`outbound`: CL 140, fix round R). */
export type AuditEventClassView = "voice_agent" | "outbound" | "billing" | "compliance" | "person";

export type AuditActorKindView = "person" | "operator" | "system";

/**
 * Where an entry came from, as the API sends it (design §15.6.9). A screen compares THIS — never
 * the slice's wording of it in `fromText` — to decide what an actor is: `api_key` is a key, never a
 * person, and `wetalk_support` is a WeTalk operator (register 0z.3).
 */
export type AuditOriginView = "console" | "api_key" | "wetalk_support" | "system";

export interface AuditEntryRowView {
  readonly auditEntryId: string;
  readonly whenText: string;
  /**
   * "Giulia Rossi", "Nightly CRM sync", "WeTalk". On an operator's entry this is the OPERATOR
   * (IC-13: `detail.operator_display_name`), never the member they were acting as.
   */
  readonly actorName: string;
  readonly actorKind: AuditActorKindView;
  readonly origin: AuditOriginView;
  /** The sentence under "What they did", already interpolated with its detail. */
  readonly summary: string;
  readonly actionCode: string;
  readonly eventClass: AuditEventClassView;
  /** "82.14.··.·· · San Francisco", or "Automation" when there is no request behind it. */
  readonly fromText: string;
  readonly iconName: string;
  /**
   * Set when a WeTalk operator was acting as a member (AC-33).
   *
   * The entry names the operator and outlives the grant, so this stays populated after the
   * impersonation has ended — which is the whole point of it being in the customer's log.
   */
  readonly impersonatedByOperatorName: string | null;
  // ---- plan-014 C4b (FR12-SEC-08): the entry's provenance.
  /** What came of the action: a refusal and a failure are recorded too. */
  readonly result: AuditResultView;
  /** The real actor's person id when they are a person (the "Who" filter offers them); never an operator's. */
  readonly realActorPersonId: string | null;
  /**
   * The person the action was taken AS when that is not the real actor — the member a WeTalk operator acted as, the
   * owner of the key that acted — by name; `null` when there is none. An unnamed one is worded by the slice.
   */
  readonly effectiveActorName: string | null;
  readonly effectiveActorPersonId: string | null;
  /** "Organization", "Workspace", "Balance" — what the action was about. */
  readonly scopeText: string;
  /** The reason the actor gave (an operator always gives one), or `null`. */
  readonly reason: string | null;
  /** The safe fields that changed, before → after, in the order the entry lists them; empty when none were recorded. */
  readonly change: readonly AuditChangeView[];
  /** plan-025 (design §36.10): the person made this change by confirming a wetalk help card ("via wetalk help"). */
  readonly viaHelp?: boolean;
}

/** plan-014 C4b: `audit_entry.result`. */
export type AuditResultView = "succeeded" | "refused" | "failed";

/** One field of an entry's safe before/after values, worded for reading. */
export interface AuditChangeView {
  readonly field: string;
  /** `null` when the field did not exist before. */
  readonly beforeText: string | null;
  /** `null` when the field does not exist after. */
  readonly afterText: string | null;
}

/** plan-014 C4b: which log — the workspace's own (RB-29), or the organization's across every workspace. */
export type AuditEntryScopeView = { readonly kind: "workspace" } | { readonly kind: "organization"; readonly accountId: string };

/** plan-014 C4b: the filters beside the heading. Every one optional; `null` is "any". */
export interface AuditFilterView {
  readonly actorPersonId: string | null;
  readonly result: AuditResultView | null;
  /** ISO-8601 instants: `from` inclusive, `to` exclusive. */
  readonly from: string | null;
  readonly to: string | null;
}

export const NO_AUDIT_FILTER: AuditFilterView = Object.freeze({ actorPersonId: null, result: null, from: null, to: null });

export interface AuditQueryView {
  /** `null` is "Everything". */
  readonly eventClass: AuditEventClassView | null;
  readonly cursor: string | null;
  /** plan-014 C4b. Absent is the workspace log, as RB-29 built it. */
  readonly log?: AuditEntryScopeView;
  /** plan-014 C4b. Absent is no filter beyond the heading. */
  readonly filter?: AuditFilterView;
}

export interface AuditEntryPageView {
  readonly row: readonly AuditEntryRowView[];
  readonly nextCursor: string | null;
  readonly matchedCount: number;
  /** What the kicker prints: the customer-facing figure, in months. */
  readonly retentionMonth: number;
  /** plan-014 C4b: more entries match than the API counts (`matchedCount` is then its ceiling). */
  readonly matchedCountCapped: boolean;
  /** plan-014 C4b: the reader sees the organization's financial entries only (Finance). */
  readonly financeOnly: boolean;
}

/**
 * A generated file the server holds for a short while.
 *
 * An **asked-for artefact with an expiry**, not a URL the console composes: the rows are
 * tenant data, the link is a capability, and a console that built the path itself would be
 * building a storage path — which only `packages/tenant` may do.
 */
export interface ExportArtefactView {
  readonly downloadUrl: string;
  readonly fileExtension: string;
  readonly expiresAtText: string;
  readonly rowCount: number;
}

// ---------------------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------------------

const EVENT_CLASS_COPY = () => (new Map<string, string>([
  ["voice_agent", portalText("shell", "agents_64acf7e2")],
  ["outbound", portalText("shell", "outbound_2c7807c0")],
  ["billing", portalText("shell", "billing_abaec452")],
  ["compliance", portalText("shell", "compliance_68f0ae49")],
  ["person", portalText("shell", "people_b37554f6")],
]));

/** The filter labels, in the order the design shows them, with "Everything" first. */
export const AUDIT_FILTER: readonly { readonly value: AuditEventClassView | null; readonly label: string }[] =
  [
    validatedDisplayView({ value: null, get label() { return portalText("shell", "everything_4a66d148"); } }),
    // Customer-portal review 2026-09-25, CL 140 (fix round R): contact lists and campaigns have
    // their own heading, so an owner looking for "who removed that list" finds it under Outbound.
    validatedDisplayView({ value: "voice_agent", get label() { return portalText("shell", "agents_64acf7e2"); } }),
    validatedDisplayView({ value: "outbound", get label() { return portalText("shell", "outbound_2c7807c0"); } }),
    validatedDisplayView({ value: "billing", get label() { return portalText("shell", "billing_abaec452"); } }),
    validatedDisplayView({ value: "compliance", get label() { return portalText("shell", "compliance_68f0ae49"); } }),
    validatedDisplayView({ value: "person", get label() { return portalText("shell", "people_b37554f6"); } }),
  ];

export function auditEventClassLabel(eventClass: string): string {
  return EVENT_CLASS_COPY().get(eventClass) ?? eventClass;
}

const RETENTION_CLASS_COPY = () => (new Map<string, string>([
  ["recording", portalText("shell", "call_recordings_bbedf3df")],
  ["transcript", portalText("shell", "transcripts_b0f28699")],
  ["order", portalText("shell", "orders_and_survey_answers_c7e56d59")],
  ["survey_result", portalText("shell", "survey_answers_73aad8af")],
  ["caller_detail", portalText("shell", "caller_details_3c23c303")],
  ["audit", portalText("shell", "audit_log_3cfc5f1c")],
]));

export function retentionClassLabel(retentionClass: string): string {
  return RETENTION_CLASS_COPY().get(retentionClass) ?? retentionClass;
}

/** "30 days", "12 months", or the sentinel's own sentence. */
export function retainDayLabel(retainDay: number): string {
  if (retainDay === RETAIN_FOREVER_DAY) return portalText("shell", "keep_until_i_delete_them_34564719");
  if (retainDay % 365 === 0 && retainDay >= 365) {
    const year = retainDay / 365;
    return year === 1 ? portalText("shell", "12_months_f7fae9b5") : portalText("shell", "value_months_d3565b83", { v0: String(String(year * 12)) });
  }
  return portalText("shell", "value_days_e2bb1259", { v0: String(String(retainDay)) });
}

/**
 * What the retention note says about the sweep.
 *
 * It says "automatically", and the thing that makes that true is an application job on the
 * outbox dispatcher rather than a storage lifecycle rule (AC-35). The customer does not need
 * to know which; they need it to be true.
 */
export const RETENTION_SWEEP_NOTE =
  () => (portalText("shell", "deleted_automatically_once_the_time_you_choose_has_passed_f8775b69"));

/** The operator-access disclosure, stated in full because it is a permission the customer grants. */
export const OPERATOR_ACCESS_NOTE =
  () => (portalText("shell", "wetalk_support_staff_can_open_a_recording_when_they_are_he_57a53c15"));

/** AC-34, in the words the Compliance screen uses. */
export const PERSON_DATA_NOTE =
  () => (portalText("shell", "deletion_removes_recordings_transcripts_orders_and_survey_20d686fb"));

/** A spend figure rendered beside a retention choice, when one is shown. */
export type RetentionSpendHint = Money | null;
